// File: frontend/src/components/STIX/BundleTable.tsx
import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Typography,
  Box,
  Chip,
  Stack,
  alpha,
  styled,
  IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import { STIXBundleEnriched } from '../../types/stix';
import { cyber } from '../../theme';

interface BundleTableProps {
  bundles: STIXBundleEnriched[];
  onPreview: (bundle: STIXBundleEnriched) => void;
  onDownload: (articleId: string, title?: string) => void;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${cyber.border}`,
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  '& .MuiTableHead-root': {
    background: alpha(theme.palette.primary.main, 0.05),
  },
  '& .MuiTableRow-root': {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.03),
    }
  }
}));

const ObjectTypeChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.25),
  fontSize: '0.7rem',
  height: '20px',
}));

const BatchActionsBar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  padding: theme.spacing(2),
  background: cyber.gradient,
  boxShadow: cyber.glowStrong,
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10,
}));

const getObjectTypeIcon = (type: string): string => {
  const icons: { [key: string]: string } = {
    'indicator': '🛡️',
    'malware': '🦠',
    'vulnerability': '🐛',
    'attack-pattern': '🎯',
    'threat-actor': '👤',
    'relationship': '🔗',
    'tool': '🔧',
    'report': '📄'
  };
  return icons[type] || '📦';
};

const getObjectTypeColor = (type: string) => {
  const colors: { [key: string]: any } = {
    'indicator': 'primary',
    'malware': 'error',
    'vulnerability': 'warning',
    'attack-pattern': 'secondary',
    'threat-actor': 'info',
    'relationship': 'default'
  };
  return colors[type] || 'default';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const BundleTable: React.FC<BundleTableProps> = ({
  bundles,
  onPreview,
  onDownload
}) => {
  const [selectedBundles, setSelectedBundles] = useState<Set<string>>(new Set());

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedBundles(new Set(bundles.map(b => b.id)));
    } else {
      setSelectedBundles(new Set());
    }
  };

  const handleSelectBundle = (bundleId: string) => {
    const newSelected = new Set(selectedBundles);
    if (newSelected.has(bundleId)) {
      newSelected.delete(bundleId);
    } else {
      newSelected.add(bundleId);
    }
    setSelectedBundles(newSelected);
  };

  const handleDownloadSelected = () => {
    selectedBundles.forEach(bundleId => {
      const bundle = bundles.find(b => b.id === bundleId);
      if (bundle) {
        onDownload(bundle.articleId, bundle.title);
      }
    });
  };

  const isSelected = (bundleId: string) => selectedBundles.has(bundleId);

  if (bundles.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No bundles found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <StyledPaper>
        <TableContainer>
          <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedBundles.size > 0 && selectedBundles.size < bundles.length}
                  checked={bundles.length > 0 && selectedBundles.size === bundles.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">Report Title</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">Date</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">Objects</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="bold">Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bundles.map((bundle) => (
              <TableRow key={bundle.id} selected={isSelected(bundle.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected(bundle.id)}
                    onChange={() => handleSelectBundle(bundle.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      {bundle.title || 'Untitled Report'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: cyber.fontMono, fontSize: '0.7rem', color: alpha(cyber.accent.cyan, 0.75) }}>
                      {bundle.articleId}
                    </Typography>
                    {bundle.sourceFeedName && (
                      <Chip
                        label={bundle.sourceFeedName}
                        size="small"
                        sx={{
                          ml: 1,
                          height: '18px',
                          fontSize: '0.65rem',
                          fontFamily: cyber.fontMono,
                          color: cyber.accent.cyan,
                          bgcolor: alpha(cyber.accent.cyan, 0.12),
                          border: `1px solid ${alpha(cyber.accent.cyan, 0.4)}`,
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(bundle.createdDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" flexWrap="wrap" spacing={0.5}>
                    {Object.entries(bundle.objectCounts)
                      .filter(([_, count]) => count > 0)
                      .map(([type, count]) => (
                        <ObjectTypeChip
                          key={type}
                          label={`${getObjectTypeIcon(type)} ${count}`}
                          color={getObjectTypeColor(type)}
                          size="small"
                          title={`${type}: ${count}`}
                        />
                      ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Preview Structure">
                      <IconButton
                        size="small"
                        onClick={() => onPreview(bundle)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download JSON">
                      <IconButton
                        size="small"
                        onClick={() => onDownload(bundle.articleId, bundle.title)}
                        color="success"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Processed Report">
                      <IconButton
                        size="small"
                        component="a"
                        href={`/report/${bundle.articleId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="info"
                      >
                        <ArticleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {bundle.articleUrl && (
                      <Tooltip title="View Original Article">
                        <IconButton
                          size="small"
                          component="a"
                          href={bundle.articleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </StyledPaper>

      {/* Batch Actions Bar */}
      {selectedBundles.size > 0 && (
        <BatchActionsBar elevation={0}>
          <Typography variant="body1" fontWeight="bold" color="white">
            {selectedBundles.size} bundle{selectedBundles.size > 1 ? 's' : ''} selected
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSelected}
            >
              Download Selected ({selectedBundles.size})
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setSelectedBundles(new Set())}
            >
              Deselect All
            </Button>
          </Stack>
        </BatchActionsBar>
      )}
    </>
  );
};

export default BundleTable;
